import React, { useEffect, useRef, useState } from "react";
import {
  Button, DatePicker, Form, Input, message, Modal, Select, Switch, Table, Tag, Typography,
} from "antd";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { hasPermission } from "../auth/permissions";
import { useAuth } from "../auth/session";
import { listUsers } from "../services/userApi";
import { createIpAccessRule, listIpAccessRules, updateIpAccessRule } from "../services/ipAccessApi";

const EMPTY_PAGE = { content: [], page: 0, size: 20, totalElements: 0 };
const DATE_TIME = new Intl.DateTimeFormat("vi-VN", { dateStyle: "short", timeStyle: "short" });

function payloadFrom(values) {
  return {
    userId: values.userId,
    ipAddress: values.ipAddress.trim(),
    validFrom: values.window[0].toISOString(),
    validThrough: values.window[1].toISOString(),
    active: Boolean(values.active),
    note: values.note?.trim() || null,
  };
}

export default function IpAccessRules() {
  const { user } = useAuth();
  const canEdit = hasPermission(user, "ip-access:edit");
  const [pageData, setPageData] = useState(EMPTY_PAGE);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingRef = useRef(false);
  const [form] = Form.useForm();

  async function load(page = 0, size = pageData.size || 20) {
    setLoading(true);
    try {
      const [rules, userPage] = await Promise.all([
        listIpAccessRules({ page, size }),
        canEdit ? listUsers({ page: 0, size: 100 }) : Promise.resolve({ content: [] }),
      ]);
      setPageData(rules);
      setUsers(userPage.content || []);
    } catch (error) {
      message.error(error.message || "Không thể tải cấu hình IP");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(0, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      active: true,
      window: [moment(), moment().add(3, "hours")],
    });
    setModalOpen(true);
  }

  function openEdit(record) {
    setEditing(record);
    form.setFieldsValue({
      userId: record.userId,
      ipAddress: record.ipAddress,
      window: [moment(record.validFrom), moment(record.validThrough)],
      active: record.active,
      note: record.note,
    });
    setModalOpen(true);
  }

  async function save(values) {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setSaving(true);
    try {
      const payload = payloadFrom(values);
      if (editing) await updateIpAccessRule(editing.id, payload);
      else await createIpAccessRule(payload);
      message.success(editing ? "Đã cập nhật IP" : "Đã thêm IP được phép");
      setModalOpen(false);
      setEditing(null);
      await load(pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể lưu cấu hình IP");
    } finally {
      pendingRef.current = false;
      setSaving(false);
    }
  }

  const columns = [
    {
      title: "Người dùng",
      render: (_, row) => row.displayName || row.username || row.userId,
    },
    { title: "IP", dataIndex: "ipAddress", render: (value) => <span className="sensitive-value">{value}</span> },
    {
      title: "Hiệu lực từ",
      dataIndex: "validFrom",
      render: (value) => DATE_TIME.format(new Date(value)),
    },
    {
      title: "Hiệu lực đến",
      dataIndex: "validThrough",
      render: (value) => DATE_TIME.format(new Date(value)),
    },
    {
      title: "Trạng thái",
      dataIndex: "active",
      render: (active) => <Tag color={active ? "green" : "default"}>{active ? "Đang áp dụng" : "Tạm tắt"}</Tag>,
    },
    { title: "Ghi chú", dataIndex: "note", render: (value) => value || "—" },
    ...(canEdit ? [{
      title: "Thao tác",
      width: 110,
      render: (_, record) => <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>Sửa</Button>,
    }] : []),
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Bảo mật IP</Typography.Title>
          <Typography.Text type="secondary">
            Khi người dùng có IP active, hệ thống chỉ cho phép truy cập đúng IP trong khoảng thời gian hiệu lực.
          </Typography.Text>
        </div>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm IP</Button>}
      </div>

      <div className="filter-bar">
        <Button icon={<ReloadOutlined />} onClick={() => load(pageData.page, pageData.size)}>Tải lại</Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={pageData.content}
        loading={loading}
        scroll={{ x: 950 }}
        pagination={{
          current: pageData.page + 1,
          pageSize: pageData.size,
          total: pageData.totalElements,
          showSizeChanger: true,
          showTotal: (total) => `${total} cấu hình`,
          onChange: (page, size) => load(page - 1, size),
        }}
      />

      <Modal
        title={editing ? "Cập nhật IP được phép" : "Thêm IP được phép"}
        open={modalOpen}
        onCancel={() => !saving && setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false} disabled={saving}>
          <Form.Item name="userId" label="Người dùng" rules={[{ required: true, message: "Chọn người dùng" }]}>
            <Select showSearch optionFilterProp="children" placeholder="Chọn user">
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.displayName || user.username} · @{user.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="ipAddress" label="IP được phép" rules={[{ required: true, whitespace: true, message: "Nhập IP" }]}>
            <Input placeholder="Ví dụ: 14.231.12.34" maxLength={45} />
          </Form.Item>
          <Form.Item name="window" label="Thời gian hiệu lực" rules={[{ required: true, message: "Chọn thời gian hiệu lực" }]}>
            <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="active" label="Áp dụng" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
